import React, { useEffect, useRef, useState } from "react";
import { useUIEventBusContext } from "@codenexus/react";
import { currentSections } from "./workProductContext";
import { getHostDocumentBridge } from "./HostDocumentBridgeProvider";
import "./StagedEditChips.css";

interface Props {
    /** docId of the section currently on screen, or null. */
    activeDocId: string | null;
    /** Navigate to a section by sectionId (docId minus the `section-` prefix). */
    onNavigate: (sectionId: string) => void;
}

/** Narrows an unknown bus payload down to a well-formed staged-edit event. */
function isStagedEditPayload(raw: unknown): raw is { editId: string; docId: string; status: "staged" } {
    if (!raw || typeof raw !== "object") return false;
    const payload = raw as Record<string, unknown>;
    return payload.status === "staged" && typeof payload.editId === "string" && payload.editId.length > 0
        && typeof payload.docId === "string" && payload.docId.length > 0;
}

/**
 * Deterministic "Review N edits in <Section>" chips. Counts staged
 * document:proposed-edit events per docId; a chip renders for every
 * section that is not currently on screen and has not yet been reviewed.
 *
 * A section's count is cleared (and stays cleared) the moment the user
 * reviews it, via either path:
 *   - clicking its chip (navigates + zeroes it), or
 *   - navigating to it some other way (e.g. the sidebar) so `activeDocId`
 *     becomes that section's docId — an effect zeroes it there too, so it
 *     does not reappear when the user later navigates away again.
 *
 * Counts are intentionally NOT reset on agent-turn boundaries: page-welcome
 * orchestration can auto-fire a new turn on navigation, and a turn-based
 * reset would wipe the chip for the document turn the user just finished
 * and is navigating away from to review.
 *
 * When there is no workproduct context at all (`currentSections()` is
 * empty — e.g. the user backed out to the projects list), chips have
 * nowhere to navigate back to, so nothing renders.
 */
export function StagedEditChips({ activeDocId, onNavigate }: Props) {
    const bus = useUIEventBusContext();
    const [counts, setCounts] = useState<Record<string, number>>({});
    const editDocuments = useRef(new Map<string, string>());

    useEffect(() => {
        if (!bus) return;
        // The bus is untyped (`UIEventMap = Record<string, unknown>`), so the
        // listener receives `unknown`. Validate the shape before counting —
        // a malformed/partial payload is ignored silently rather than
        // crashing on a bad cast.
        return bus.on("document:proposed-edit", raw => {
            if (!isStagedEditPayload(raw)) return;
            // A repeated event for the same edit must not inflate the count.
            if (editDocuments.current.has(raw.editId)) return;
            editDocuments.current.set(raw.editId, raw.docId);
            setCounts(c => ({ ...c, [raw.docId]: (c[raw.docId] ?? 0) + 1 }));
        });
    }, [bus]);

    useEffect(() => {
        // Resolutions are emitted by the editor adapter, not the UI event bus.
        // Remove accepted/rejected/stale edits immediately so a hidden chip
        // cannot reappear after the user navigates away from the editor.
        return getHostDocumentBridge().composite.onEditResolved(({ editId }) => {
            const docId = editDocuments.current.get(editId);
            if (!docId) return;
            editDocuments.current.delete(editId);
            setCounts(c => ({ ...c, [docId]: Math.max(0, (c[docId] ?? 0) - 1) }));
        });
    }, []);

    useEffect(() => {
        // Visiting a section (activeDocId becomes its docId) counts as
        // reviewing it, same as clicking the chip would — clear it here too
        // so it stays cleared on the next navigate-away.
        if (!activeDocId) return;
        setCounts(c => (c[activeDocId] ? { ...c, [activeDocId]: 0 } : c));
    }, [activeDocId]);

    const sections = currentSections();
    if (sections.length === 0) return null;

    const chips = Object.entries(counts)
        .filter(([docId, n]) => n > 0 && docId !== activeDocId)
        .map(([docId, n]) => {
            const sectionId = docId.replace(/^section-/, "");
            const title = sections.find(s => s.sectionId === sectionId)?.title ?? "a section";
            return { docId, sectionId, n, title };
        });

    if (chips.length === 0) return null;
    return (
        <div className="staged-edit-chips" role="group" aria-label="Staged edits awaiting review">
            {chips.map(chip => (
                <button
                    key={chip.docId}
                    type="button"
                    className="staged-edit-chip"
                    onClick={() => {
                        setCounts(c => ({ ...c, [chip.docId]: 0 }));
                        onNavigate(chip.sectionId);
                    }}
                >
                    Review {chip.n} {chip.n === 1 ? "edit" : "edits"} in {chip.title}
                </button>
            ))}
        </div>
    );
}
